import { Injectable } from '@angular/core';
import { Logger, ServerRootService } from '@se/se.ui.html5';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject, catchError, map, Observable, of, filter } from 'rxjs';
import { ExtendedSuggestion, Suggestion } from '@se/ui.text.editor';
import {
    CLINICAL_DEFAULT_REPORT_TEE_RESPONSE,
    CLINICAL_DEFAULT_REPORT_SINGLE_GROUP_RESPONSE,
} from './mock/clinical-default-report-response';


export interface ClinicalDefaultReportResponse {
    id: string;
    code: string;
    description: string;
    texts: { [key: string]: string };
    isNormality?: boolean;
}

export interface ClinicalDefaultReportRequest {
    idReferringPhysician: string;
    groupName: string;
    idExaminations: string[];
}

export interface SelectedSuggestionEvent {
    groupNameHashCode: number;
    suggestion: Suggestion;
    fromDefaultTextName: string;
}

export interface SelectedExtendedSuggestionEvent {
    groupNameHashCode: number;
    suggestion: ExtendedSuggestion;
    fromDefaultTextName: string;
}

@Injectable({
    providedIn: 'root',
})
export class ClinicalDefaultReportService extends ServerRootService {
    private data: Map<number, Observable<Array<Suggestion>>> = new Map<number, Observable<Array<Suggestion>>>();

    // private defaultGroupName!: string;
    // private suggestions!: Observable<Array<Suggestion>>;

    private selected$: ReplaySubject<SelectedSuggestionEvent> = new ReplaySubject<SelectedSuggestionEvent>(1);
    private selectedStream: Observable<SelectedSuggestionEvent> = this.selected$.asObservable();

    private selectedExtendedSuggestion$: ReplaySubject<SelectedExtendedSuggestionEvent> = new ReplaySubject<SelectedExtendedSuggestionEvent>(1);
    private selectedExtendedSuggestionStream: Observable<SelectedExtendedSuggestionEvent> = this.selectedExtendedSuggestion$.asObservable();

    constructor(private http: HttpClient, readonly logger: Logger) {
        super();
    }

    getSuggestions(request: ClinicalDefaultReportRequest | undefined): Observable<Array<Suggestion>> | undefined {
        if (!request || !request.groupName) {
            throw new Error(`Request not present`);
        }
        const requestHashCode = this.getRequestHashCode(request);
        if (!this.data.has(requestHashCode)) {
            this.init(request);
        }
        return this.data.get(requestHashCode);
    }

    private getRequestHashCode(request: ClinicalDefaultReportRequest): number {
        return this.hashCode(JSON.stringify(request));
    }

    private init(request: ClinicalDefaultReportRequest) {
        if (!request.groupName) {
            throw new Error(`Request for DefaultGroupName without groupName`);
        }
        this.data.set(this.getRequestHashCode(request), this.callServiceAndTransform(request));
    }

    private callServiceAndTransform(request: ClinicalDefaultReportRequest): Observable<Array<Suggestion>> {
        return this.getClinicalDefaultReport(request).pipe(
            map(
                (items: Array<ClinicalDefaultReportResponse>) => {
                    return this.clinicalDefaultReportsToSuggestions(this.getRequestHashCode(request), items);
                }),
            catchError(error => {
                this.logger.warn('ClinicalDefaultReportService - impossibile recuperare i dati. ', error);
                return of([]);
            }),
        );
    }

    getSelectedSuggestion(request: ClinicalDefaultReportRequest, defaultTextName: string): Observable<SelectedExtendedSuggestionEvent> {
        return this.selectedExtendedSuggestionStream.pipe(
            filter((event: SelectedExtendedSuggestionEvent) =>
                event.groupNameHashCode === this.getRequestHashCode(request) &&
                event.fromDefaultTextName !== defaultTextName,
            ),
        );
    }

    saveSuggestion(_: Suggestion) {
        throw new Error('Method not implemented yet');
    }

    unregisterRequest(request: ClinicalDefaultReportRequest) {
        this.data.delete(this.getRequestHashCode(request));
    }

    selectSuggestion(extendedSuggestion: ExtendedSuggestion, fromDefaultTextName: string) {
        const event: SelectedExtendedSuggestionEvent = {
            groupNameHashCode: extendedSuggestion.suggestion.groupNameHashCode,
            suggestion: extendedSuggestion,
            fromDefaultTextName: fromDefaultTextName,
        };
        this.selectedExtendedSuggestion$.next(event);
    }

    private getClinicalDefaultReport(request: ClinicalDefaultReportRequest): Observable<Array<ClinicalDefaultReportResponse>> {
        // DGS per il momento manca il passaggio dei prametri per la response e usiamo dei dati mock salvati in un file invece di chiamare il BE REST
        //return this.http.get<Array<ClinicalDefaultReportResponse>>(this.getServerRoot() + 'ClinicalDefaultReport/GetClinicalDefaultReport');

        switch (request.groupName) {
            case 'TEE':
                return of(CLINICAL_DEFAULT_REPORT_TEE_RESPONSE);
            case 'TTE':
                return of(CLINICAL_DEFAULT_REPORT_TEE_RESPONSE);
            case 'SingleGroup':
                return of(CLINICAL_DEFAULT_REPORT_SINGLE_GROUP_RESPONSE);
            default:
                return of(CLINICAL_DEFAULT_REPORT_TEE_RESPONSE);
        }
    }

    private clinicalDefaultReportsToSuggestions(groupNameHashCode: number, items: Array<ClinicalDefaultReportResponse>): Array<Suggestion> {
        let idCount: number = 0;
        return items.map((item: ClinicalDefaultReportResponse) => {

            return this.clinicalDefaultReportToSuggestion(groupNameHashCode, idCount++, item);
        });
    }

    private clinicalDefaultReportToSuggestion(groupNameHashCode: number, id: number, item: ClinicalDefaultReportResponse): Suggestion {
        return {
            id: id,
            Description: item.description,
            Sentence: item.texts,
            IsNormality: item.isNormality ?? false,
            code: item.code,
            newLineAfter: false,
            groupNameHashCode: groupNameHashCode,
        };
    }

    private hashCode(value: string): number {
        var hash = 0,
            i, chr;
        if (value.length === 0) return hash;
        for (i = 0; i < value.length; i++) {
            chr = value.charCodeAt(i);
            hash = ((hash << 5) - hash) + chr;
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
}
